import React, { useMemo } from 'react';
import PropTypes from 'prop-types';
import { useSelector } from 'react-redux';

import { useGetEventsQuery } from 'common/store/modules/api/content-api';
import { navSelector } from 'common/store/modules/events-listing/selectors';

import { filterByTeam } from '../utils/filter-by-team';
import { CardSkeleton } from './cards/CardSkeleton';
import { EventsListingByFilter } from './EventsListingByFilter';
import { EventsListingByDate } from './EventsListingByDate';

export const EventsListingCards = ({
    eventId,
    compseasonId,
    localTimezone
}) => {
    const nav = useSelector(navSelector);

    const timezone = localTimezone ? localTimezone : 'Australia/Melbourne';

    const activeStartDate = `${moment(nav?.date).format(
        'YYYY-MM-DD 00:00:00'
    )}`;
    const activeEndDate = `${moment(nav?.date).format('YYYY-MM-DD 23:59:59')}`;

    const dateBounds = {
        start: moment
            .tz(activeStartDate, timezone)
            .utc()
            .format('YYYY-MM-DDTHH:mm:ss'),
        end: moment
            .tz(activeEndDate, timezone)
            .utc()
            .format('YYYY-MM-DDTHH:mm:ss')
    };

    const contentFilter = {
        rangeFilter: {
            lowerBound: {
                fieldName: 'Date & Time',
                dvalue: `${dateBounds.start}.000Z`
            },
            upperBound: {
                fieldName: 'Date & Time',
                dvalue: `${dateBounds.end}.000Z`
            }
        }
    };

    const objJsonStr = JSON.stringify(contentFilter);
    const encodedString = btoa(objJsonStr);

    const filterParams = {
        tagNames: nav.filters.eventTypes,
        contentFilter:
            !nav?.filters?.eventTypes?.length > 0 &&
            !nav?.filters?.teams?.length > 0
                ? encodedString
                : ''
    };

    const { events = [], isEventsFetching } = useGetEventsQuery(
        {
            eventId: eventId,
            params: filterParams
        },
        {
            skip: !eventId || !nav?.date,
            selectFromResult: ({ data, isFetching }) => ({
                events:
                    data && nav.filters.teams.length
                        ? filterByTeam(data, nav.filters.teams)
                        : data,
                isEventsFetching: isFetching
            })
        }
    );

    const sorted = useMemo(() => {
        return sortByDate(events);
    }, [events]);

    if (isEventsFetching) {
        return (
            <div className="events-listing wrapper">
                <div className="events-listing__header"></div>
                <CardSkeleton />
            </div>
        );
    }

    return !nav?.filters?.eventTypes?.length > 0 &&
        !nav?.filters?.teams?.length > 0 ? (
        <EventsListingByDate
            eventsArray={sorted}
            localTimezone={localTimezone}
            compseasonId={compseasonId}
            activeStartDate={activeStartDate}
        />
    ) : (
        <EventsListingByFilter
            eventsArray={sorted}
            localTimezone={localTimezone}
            compseasonId={compseasonId}
        />
    );
};

const sortByDate = (data) => {
    const events = data?.map((event) => {
        let detailsModule =
            event.modulesInContent?.find(
                (item) =>
                    item.title.toLowerCase().replace(' ', '') === 'details'
            ) ?? null;

        let date = detailsModule
            ? moment(
                  PULSE.app.common.events.getModuleItem(
                      detailsModule,
                      'Date & Time'
                  )
              )
            : null;

        return { ...event, date: date };
    });

    return events.sort((a, b) => {
        return moment(a.date).diff(b.date);
    });
};

EventsListingCards.propTypes = {
    localTimezone: PropTypes.string,
    compseasonId: PropTypes.string,
    eventId: PropTypes.string.isRequired
};
